import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { db, storage } from './firebase'; // Make sure to export 'storage' from your firebase.js
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { locations } from './tanzaniaLocations';
import './PublicReportForm.css';

const PublicReportForm = () => {
    const { t } = useTranslation();
    const [reportType, setReportType] = useState('Accident'); // 'Accident' or 'Hazard'

    const initialAccidentState = {
        datetime: '',
        source: 'User Report',
        sourceDetails: '',
        region: '',
        district: '',
        ward: '',
        location: '',
        vehicles: [{ type: 'Private Vehicle', registration: '', ownerDetails: '' }],
        injuriesMale: 0,
        injuriesFemale: 0,
        injuriesChildren: 0,
        fatalitiesMale: 0,
        fatalitiesFemale: 0,
        fatalitiesChildren: 0,
        totalInjuries: 0,
        totalFatalities: 0,
        description: '',
        isVerified: false,
        mediaUrls: [],
    };

    const initialHazardState = {
        datetime: '',
        hazardType: 'Pothole',
        description: '',
        region: '',
        district: '',
        ward: '',
        location: '',
        isVerified: false,
        isResolved: false,
        mediaUrls: [],
    };

    const [formData, setFormData] = useState(initialAccidentState);
    const [mediaFiles, setMediaFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [submissionStatus, setSubmissionStatus] = useState({ message: '', type: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [knownCasualties, setKnownCasualties] = useState({ deaths: 'Known', injuries: 'Known' });
    const [isLocating, setIsLocating] = useState(false);
    const [locationMessage, setLocationMessage] = useState('');

    const sourceOptions = [
        { value: 'User Report', key: 'user' },
        { value: 'Witness Report', key: 'witness' },
        { value: 'Victim Report', key: 'victim' },
        { value: 'Official Report', key: 'official' },
        { value: 'Social Media', key: 'social' },
        { value: 'News Outlet', key: 'news' },
        { value: 'Other', key: 'other' }
    ];
    
    const vehicleOptions = [
        { value: 'Private Vehicle', key: 'private' },
        { value: 'Motorcycle (Bodaboda)', key: 'bodaboda' },
        { value: 'Tricycle (Bajaji)', key: 'bajaji' },
        { value: 'Mini Bus (Daladala)', key: 'daladala' },
        { value: 'Regional Bus', key: 'regional_bus' },
        { value: 'Lorry', key: 'lorry' },
        { value: 'Trailer', key: 'trailer' },
        { value: 'Bicycle', key: 'bicycle' },
        { value: 'Train', key: 'train' },
        { value: 'Other', key: 'other' }
    ];

    const hazardTypes = [
        { value: 'Pothole', key: 'pothole' },
        { value: 'Broken Traffic Light', key: 'broken_light' },
        { value: 'Missing Signage', key: 'missing_sign' },
        { value: 'Road Debris', key: 'debris' },
        { value: 'Flooding', key: 'flooding' },
        { value: 'Broken Down Vehicle(s)', key: 'broken_vehicle' },
        { value: 'Other', key: 'other_hazard' }
    ];

    const handleReportTypeChange = (type) => {
        setReportType(type);
        setFormData(type === 'Accident' ? initialAccidentState : initialHazardState);
        setSubmissionStatus({ message: '', type: '' });
        setLocationMessage('');
        setMediaFiles([]);
        setImagePreviews([]);
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'number' ? Number(value) : value }));
    };

    const handleLocationChange = (e) => {
        const { name, value } = e.target;
        let newFormData = { ...formData, [name]: value };
        if (name === 'region') {
            newFormData.district = '';
            newFormData.ward = '';
        }
        if (name === 'district') {
            newFormData.ward = '';
        }
        setFormData(newFormData);
    };

    const handleVehicleChange = (index, event) => {
        const { name, value } = event.target;
        const newVehicles = formData.vehicles.map((vehicle, i) =>
            index === i ? { ...vehicle, [name]: value } : vehicle
        );
        setFormData(prev => ({ ...prev, vehicles: newVehicles }));
    };

    const addVehicle = () => {
        setFormData(prev => ({
            ...prev,
            vehicles: [...prev.vehicles, { type: 'Private Vehicle', registration: '', ownerDetails: '' }]
        }));
    };

    const removeVehicle = (index) => {
        setFormData(prev => ({
            ...prev,
            vehicles: prev.vehicles.filter((_, i) => i !== index)
        }));
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setMediaFiles(prev => [...prev, ...files]);

        const newPreviews = files.map(file => URL.createObjectURL(file));
        setImagePreviews(prev => [...prev, ...newPreviews]);
    };

    const removePreview = (index) => {
        setMediaFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => {
            const newPreviews = prev.filter((_, i) => i !== index);
            URL.revokeObjectURL(prev[index]); // Clean up memory
            return newPreviews;
        });
    };

    const handleGetLocation = () => {
        if (!navigator.geolocation) {
            setLocationMessage('Geolocation is not supported by your browser.');
            return;
        }
        setIsLocating(true);
        setLocationMessage(t('locating_button_text'));
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                setLocationMessage('Location found. Fetching address...');
                setFormData(prev => ({ ...prev, location: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}` }));
                try {
                    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`);
                    const data = await response.json();
                    if (data && data.address) {
                        const { state: regionName, state_district: districtName } = data.address;
                        const matchedRegion = Object.keys(locations).find(r => regionName.includes(r));
                        if (matchedRegion) {
                            const matchedDistrict = Object.keys(locations[matchedRegion]).find(d => (districtName || '').includes(d));
                            setFormData(prev => ({ ...prev, region: matchedRegion, district: matchedDistrict || '' }));
                            setLocationMessage('Region and District auto-filled!');
                        } else {
                            setLocationMessage('Could not match location to a known Region.');
                        }
                    } else {
                        setLocationMessage('Could not fetch address details.');
                    }
                } catch (apiError) {
                    console.error("Reverse geocoding error:", apiError);
                    setLocationMessage('Error fetching address details.');
                } finally {
                    setIsLocating(false);
                }
            },
            (error) => {
                let message = 'Could not get your location. ';
                switch (error.code) {
                    case error.PERMISSION_DENIED: message += 'You denied the request for Geolocation.'; break;
                    case error.POSITION_UNAVAILABLE: message += 'Location information is unavailable.'; break;
                    case error.TIMEOUT: message += 'The request to get user location timed out.'; break;
                    default: message += 'An unknown error occurred.'; break;
                }
                setLocationMessage(message);
                setIsLocating(false);
            }
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmissionStatus({ message: t('submitting_button_text'), type: 'info' });
        
        try {
            let mediaUrls = [];
            if (mediaFiles.length > 0) {
                setSubmissionStatus({ message: 'Uploading media...', type: 'info' });
                const uploadPromises = mediaFiles.map(file => {
                    const storageRef = ref(storage, `media/${Date.now()}_${file.name}`);
                    return uploadBytes(storageRef, file).then(snapshot => getDownloadURL(snapshot.ref));
                });
                mediaUrls = await Promise.all(uploadPromises);
            }
            
            setSubmissionStatus({ message: 'Finalizing report...', type: 'info' });

            const collectionName = reportType === 'Accident' ? 'accidents' : 'hazards';
            const dataToSubmit = { 
                ...formData, 
                datetime: Timestamp.fromDate(new Date(formData.datetime)), 
                submittedAt: Timestamp.now(),
                mediaUrls
            };
            
            await addDoc(collection(db, collectionName), dataToSubmit);
            
            setSubmissionStatus({ message: t('submission_success'), type: 'success' });
            setFormData(reportType === 'Accident' ? initialAccidentState : initialHazardState);
            setMediaFiles([]);
            setImagePreviews([]);
        } catch (error) {
            console.error("Error adding document: ", error);
            setSubmissionStatus({ message: t('submission_fail'), type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const totalFatalities = knownCasualties.deaths === 'Known' ? formData.fatalitiesMale + formData.fatalitiesFemale + formData.fatalitiesChildren : formData.totalFatalities;
    const totalInjuries = knownCasualties.injuries === 'Known' ? formData.injuriesMale + formData.injuriesFemale + formData.injuriesChildren : formData.totalInjuries;

    const renderAccidentForm = () => (
        <>
            {/* --- SECTION 1: INCIDENT DETAILS --- */}
            <div className="form-section">
                <h2>{t('incident_details_heading')}</h2>
                <div className="form-grid">
                    <div className="form-group">
                        <label>{t('date_time_label')}</label>
                        <input type="datetime-local" name="datetime" value={formData.datetime} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                        <label>{t('source_info_label')}</label>
                        <select name="source" value={formData.source} onChange={handleChange}>
                            {sourceOptions.map(option => (
                                <option key={option.key} value={option.value}>
                                    {t(`source_option_${option.key}`)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="form-group">
                    <label>{t('source_details_label')}</label>
                    <input type="text" name="sourceDetails" placeholder={t('source_details_placeholder')} value={formData.sourceDetails} onChange={handleChange} />
                </div>
            </div>
            
            {/* --- SECTION 2: LOCATION INFO --- */}
            <div className="form-section">
                <h2>{t('location_info_heading')}</h2>
                <div className="form-grid">
                    <div className="form-group">
                        <label>{t('region_label')}</label>
                        <select name="region" value={formData.region} onChange={handleLocationChange} required>
                            <option value="">{t('select_region_option')}</option>
                            {Object.keys(locations).map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>{t('district_optional_label')}</label>
                        <select name="district" value={formData.district} onChange={handleLocationChange} disabled={!formData.region}>
                            <option value="">{t('select_district_option')}</option>
                            {formData.region && Object.keys(locations[formData.region]).map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>
                </div>
                <div className="form-group">
                    <label>{t('ward_optional_label')}</label>
                    <select name="ward" value={formData.ward} onChange={handleChange} disabled={!formData.district}>
                        <option value="">{t('other_unknown_option')}</option>
                        {formData.district && locations[formData.region][formData.district] && locations[formData.region][formData.district].map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                </div>
                <div className="form-group">
                    <label>{t('specific_landmark_label')}</label>
                    <div className="location-input-group">
                        <button type="button" onClick={handleGetLocation} className="btn-location" disabled={isLocating}>
                            {isLocating ? t('locating_button_text') : t('use_my_location_button')}
                        </button>
                    </div>
                    {locationMessage && <p className="location-message">{locationMessage}</p>}
                    <input type="text" name="location" placeholder={t('specific_location_placeholder')} value={formData.location} onChange={handleChange} required />
                </div>
            </div>

            {/* --- SECTION 3: VEHICLES INVOLVED --- */}
            <div className="form-section">
                <h2>{t('vehicles_heading')}</h2>
                {formData.vehicles.map((vehicle, index) => (
                    <div className="vehicle-entry" key={index}>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>{t('vehicle_number_type_label')} {index + 1} Type</label>
                                <select name="type" value={vehicle.type} onChange={(e) => handleVehicleChange(index, e)}>
                                    {vehicleOptions.map(option => (
                                        <option key={option.key} value={option.value}>
                                            {t(`vehicle_option_${option.key}`)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>{t('vehicle_reg_label')}</label>
                                <input type="text" name="registration" placeholder={t('vehicle_reg_placeholder')} value={vehicle.registration} onChange={(e) => handleVehicleChange(index, e)}/>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>{t('owner_details_label')}</label>
                            <input type="text" name="ownerDetails" placeholder={t('owner_details_placeholder')} value={vehicle.ownerDetails} onChange={(e) => handleVehicleChange(index, e)}/>
                        </div>
                        {formData.vehicles.length > 1 && (
                            <button type="button" className="remove-btn" onClick={() => removeVehicle(index)}>{t('remove_vehicle_number_button')} {index + 1}</button>
                        )}
                    </div>
                ))}
                <button type="button" className="add-btn" onClick={addVehicle}>{t('add_vehicle_button')}</button>
            </div>

            {/* --- SECTION 4: CASUALTIES --- */}
            <div className="form-section">
                <h2>{t('casualties_heading')}</h2>
                <div className="casualty-section">
                    <div className="casualty-header">
                        <h3>{t('deaths_heading')}</h3>
                        <div className="toggle-buttons">
                            <button type="button" className={knownCasualties.deaths === 'Known' ? 'active' : ''} onClick={() => setKnownCasualties(prev => ({...prev, deaths: 'Known'}))}>{t('known_button')}</button>
                            <button type="button" className={knownCasualties.deaths === 'Unknown' ? 'active' : ''} onClick={() => setKnownCasualties(prev => ({...prev, deaths: 'Unknown'}))}>{t('unknown_button')}</button>
                        </div>
                    </div>
                    {knownCasualties.deaths === 'Known' ? (
                        <div className="form-grid casualty-grid">
                            <div className="form-group"><label>{t('male_label')}</label><input type="number" min="0" name="fatalitiesMale" value={formData.fatalitiesMale} onChange={handleChange}/></div>
                            <div className="form-group"><label>{t('female_label')}</label><input type="number" min="0" name="fatalitiesFemale" value={formData.fatalitiesFemale} onChange={handleChange}/></div>
                            <div className="form-group"><label>{t('children_label')}</label><input type="number" min="0" name="fatalitiesChildren" value={formData.fatalitiesChildren} onChange={handleChange}/></div>
                        </div>
                    ) : (
                        <div className="form-group"><label>{t('total_deaths_label')}</label><input type="number" min="0" name="totalFatalities" value={formData.totalFatalities} onChange={handleChange}/></div>
                    )}
                    <p className="total-display">{t('total_prefix')} {totalFatalities}</p>
                </div>
                <div className="casualty-section">
                     <div className="casualty-header">
                        <h3>{t('injuries_heading')}</h3>
                        <div className="toggle-buttons">
                            <button type="button" className={knownCasualties.injuries === 'Known' ? 'active' : ''} onClick={() => setKnownCasualties(prev => ({...prev, injuries: 'Known'}))}>{t('known_button')}</button>
                            <button type="button" className={knownCasualties.injuries === 'Unknown' ? 'active' : ''} onClick={() => setKnownCasualties(prev => ({...prev, injuries: 'Unknown'}))}>{t('unknown_button')}</button>
                        </div>
                    </div>
                    {knownCasualties.injuries === 'Known' ? (
                        <div className="form-grid casualty-grid">
                            <div className="form-group"><label>{t('male_label')}</label><input type="number" min="0" name="injuriesMale" value={formData.injuriesMale} onChange={handleChange}/></div>
                            <div className="form-group"><label>{t('female_label')}</label><input type="number" min="0" name="injuriesFemale" value={formData.injuriesFemale} onChange={handleChange}/></div>
                            <div className="form-group"><label>{t('children_label')}</label><input type="number" min="0" name="injuriesChildren" value={formData.injuriesChildren} onChange={handleChange}/></div>
                        </div>
                    ) : (
                        <div className="form-group"><label>{t('total_injuries_label')}</label><input type="number" min="0" name="totalInjuries" value={formData.totalInjuries} onChange={handleChange}/></div>
                    )}
                    <p className="total-display">{t('total_prefix')} {totalInjuries}</p>
                </div>
            </div>
        </>
    );

    const renderHazardForm = () => (
        <div className="form-section">
            <h2>{t('hazard_details_heading')}</h2>
            <div className="form-grid">
                <div className="form-group">
                    <label>{t('date_time_label')}</label>
                    <input type="datetime-local" name="datetime" value={formData.datetime} onChange={handleChange} required />
                </div>
                <div className="form-group">
                    <label>{t('hazard_type_label')}</label>
                    <select name="hazardType" value={formData.hazardType} onChange={handleChange}>
                        {hazardTypes.map(option => (
                            <option key={option.key} value={option.value}>
                                {t(`hazard_type_${option.key}`)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );

    return (
        <>
        <style>{`
            .report-type-toggle {
                display: flex;
                margin-bottom: 2rem;
                border-radius: 8px;
                overflow: hidden;
            }
            .report-type-toggle button {
                flex: 1;
                padding: 15px 10px;
                font-size: 1rem;
                font-weight: 500;
                border: 1px solid #ccc;
                background-color: #f0f0f0;
                color: #555;
                cursor: pointer;
                transition: background-color 0.3s ease, color 0.3s ease;
            }
            .report-type-toggle button:first-of-type {
                border-right: none;
            }
            .report-type-toggle button.active {
                background-color: #6c757d;
                color: white;
                border-color: #6c757d;
            }
            .image-previews {
                display: flex;
                flex-wrap: wrap;
                gap: 10px;
                margin-top: 10px;
            }
            .preview-item {
                position: relative;
                width: 100px;
                height: 100px;
            }
            .preview-item img, .preview-item video {
                width: 100%;
                height: 100%;
                object-fit: cover;
                border-radius: 8px;
            }
            .preview-item .remove-preview-btn {
                position: absolute;
                top: -5px;
                right: -5px;
                background: red;
                color: white;
                border: none;
                border-radius: 50%;
                width: 20px;
                height: 20px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
            }
        `}</style>
        <form onSubmit={handleSubmit} className="public-report-form">
            <h1>{t('report_form_title')}</h1>
            <p className="form-description">{t('report_form_intro_detailed')}</p>

            <div className="report-type-toggle">
                <button type="button" className={reportType === 'Accident' ? 'active' : ''} onClick={() => handleReportTypeChange('Accident')}>
                    {t('report_accident_button')}
                </button>
                <button type="button" className={reportType === 'Hazard' ? 'active' : ''} onClick={() => handleReportTypeChange('Hazard')}>
                    {t('report_hazard_button')}
                </button>
            </div>
            
            {reportType === 'Accident' ? renderAccidentForm() : renderHazardForm()}

            {reportType === 'Hazard' && (
                 <div className="form-section">
                    <h2>{t('location_info_heading')}</h2>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>{t('region_label')}</label>
                            <select name="region" value={formData.region} onChange={handleLocationChange} required>
                                <option value="">{t('select_region_option')}</option>
                                {Object.keys(locations).map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>{t('district_optional_label')}</label>
                            <select name="district" value={formData.district} onChange={handleLocationChange} disabled={!formData.region}>
                                <option value="">{t('select_district_option')}</option>
                                {formData.region && Object.keys(locations[formData.region]).map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>{t('ward_optional_label')}</label>
                        <select name="ward" value={formData.ward} onChange={handleChange} disabled={!formData.district}>
                            <option value="">{t('other_unknown_option')}</option>
                            {formData.district && locations[formData.region][formData.district] && locations[formData.region][formData.district].map(w => <option key={w} value={w}>{w}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>{t('specific_landmark_label')}</label>
                        <div className="location-input-group">
                            <button type="button" onClick={handleGetLocation} className="btn-location" disabled={isLocating}>
                                {isLocating ? t('locating_button_text') : t('use_my_location_button')}
                            </button>
                        </div>
                        {locationMessage && <p className="location-message">{locationMessage}</p>}
                        <input type="text" name="location" placeholder={t('specific_location_placeholder')} value={formData.location} onChange={handleChange} required />
                    </div>
                </div>
            )}

            <div className="form-section">
                <h2>{t('media_upload_heading')}</h2>
                <div className="form-group">
                    <label>{t('media_upload_label')}</label>
                    <input type="file" multiple accept="image/*,video/*" onChange={handleFileChange} className="file-input"/>
                    <p className="form-hint">{t('media_upload_hint')}</p>
                </div>
                {imagePreviews.length > 0 && (
                    <div className="image-previews">
                        {imagePreviews.map((preview, index) => (
                            <div key={index} className="preview-item">
                                {mediaFiles[index].type.startsWith('video') ? (
                                    <video src={preview} controls />
                                ) : (
                                    <img src={preview} alt={`preview ${index}`} />
                                )}
                                <button type="button" className="remove-preview-btn" onClick={() => removePreview(index)}>×</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="form-section">
                <h2>{t('description_heading')}</h2>
                <div className="form-group">
                    <textarea name="description" rows="4" placeholder={t('description_placeholder')} value={formData.description} onChange={handleChange}></textarea>
                </div>
            </div>

            <button type="submit" className="submit-btn" disabled={isSubmitting}>{isSubmitting ? t('submitting_button_text') : t('submit_for_review_button')}</button>
            {submissionStatus.message && <p className={`submission-status ${submissionStatus.type}`}>{submissionStatus.message}</p>}
        </form>
        </>
    );
};

export default PublicReportForm;
